/**
 * Escape CSV value
 */
function escapeCSV(value, delimiter = ',') {
    if (value === null || value === undefined)
        return '';
    const str = String(value);
    if (str.includes(delimiter) || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
}
/**
 * CSV Export functionality
 */
export function exportToCSV(data, columns, options = {}) {
    const { filename = 'export.csv', includeHeaders = true, delimiter = ',' } = options;
    const rows = [];
    if (includeHeaders) {
        rows.push(columns.map(col => escapeCSV(col, delimiter)).join(delimiter));
    }
    for (const row of data) {
        const values = columns.map(col => escapeCSV(row[col], delimiter));
        rows.push(values.join(delimiter));
    }
    const csvContent = rows.join('\n');
    downloadFile(csvContent, filename, 'text/csv;charset=utf-8;');
}
/**
 * Export pivot table to CSV
 */
export function exportPivotToCSV(pivotData, rowFields, _columnFields, valueFields, options = {}) {
    const { filename = 'pivot-export.csv', delimiter = ',' } = options;
    const rows = [];
    const { headers, rowHeaders, data, rowTotals, columnTotals, grandTotal, showRowTotals, showColumnTotals } = pivotData;
    // Calculate number of row header columns
    const rowHeaderColCount = rowFields.length || 1;
    // Build column headers
    if (headers.length > 0) {
        // Multi-level column headers
        for (let level = 0; level < headers.length; level++) {
            const headerRow = [];
            // Empty cells for row field columns
            for (let i = 0; i < rowHeaderColCount; i++) {
                headerRow.push(level === headers.length - 1 ? escapeCSV(rowFields[i] || '', delimiter) : '');
            }
            // Column header values
            for (const val of headers[level]) {
                headerRow.push(escapeCSV(val, delimiter));
            }
            // Row totals header
            if (showRowTotals && rowTotals && rowTotals.length > 0) {
                if (level === headers.length - 1) {
                    for (const vf of valueFields) {
                        headerRow.push(escapeCSV(`Total (${vf.aggregation})`, delimiter));
                    }
                }
                else {
                    for (let i = 0; i < valueFields.length; i++) {
                        headerRow.push('');
                    }
                }
            }
            rows.push(headerRow.join(delimiter));
        }
    }
    else {
        // Simple header with value fields only
        const headerRow = [];
        for (let i = 0; i < rowHeaderColCount; i++) {
            headerRow.push(escapeCSV(rowFields[i] || '', delimiter));
        }
        for (const vf of valueFields) {
            headerRow.push(escapeCSV(`${vf.field} (${vf.aggregation})`, delimiter));
        }
        if (showRowTotals && rowTotals && rowTotals.length > 0) {
            headerRow.push(escapeCSV('Total', delimiter));
        }
        rows.push(headerRow.join(delimiter));
    }
    // Build data rows
    for (let rowIdx = 0; rowIdx < rowHeaders.length; rowIdx++) {
        const csvRow = [];
        // Row headers
        const rowHeader = rowHeaders[rowIdx] || [];
        for (let i = 0; i < rowHeaderColCount; i++) {
            csvRow.push(escapeCSV(rowHeader[i] || '', delimiter));
        }
        // Data cells
        const rowData = data[rowIdx] || [];
        for (const cell of rowData) {
            csvRow.push(escapeCSV(cell?.formattedValue || '', delimiter));
        }
        // Row total
        if (showRowTotals && rowTotals && rowTotals[rowIdx]) {
            csvRow.push(escapeCSV(rowTotals[rowIdx].formattedValue || '', delimiter));
        }
        rows.push(csvRow.join(delimiter));
    }
    // Column totals row
    if (showColumnTotals && columnTotals && columnTotals.length > 0) {
        const totalsRow = [];
        // Label for totals row
        totalsRow.push(escapeCSV('Total', delimiter));
        for (let i = 1; i < rowHeaderColCount; i++) {
            totalsRow.push('');
        }
        // Column total values
        for (const cell of columnTotals) {
            totalsRow.push(escapeCSV(cell?.formattedValue || '', delimiter));
        }
        // Grand total
        if (showRowTotals && grandTotal) {
            totalsRow.push(escapeCSV(grandTotal.formattedValue || '', delimiter));
        }
        rows.push(totalsRow.join(delimiter));
    }
    const csvContent = rows.join('\n');
    downloadFile(csvContent, filename, 'text/csv;charset=utf-8;');
}
/**
 * Download file helper
 */
export function downloadFile(content, filename, mimeType) {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}
/**
 * Copy text to clipboard
 */
export function copyToClipboard(text, onSuccess, onError) {
    navigator.clipboard.writeText(text).then(onSuccess).catch(onError);
}
/**
 * Format selected cells for clipboard (tab-separated)
 */
export function formatSelectionForClipboard(rows, columns, selectionBounds) {
    const { minRow, maxRow, minCol, maxCol } = selectionBounds;
    const lines = [];
    for (let r = minRow; r <= maxRow; r++) {
        const row = rows[r];
        if (!row)
            continue;
        const values = [];
        for (let c = minCol; c <= maxCol; c++) {
            const colId = columns[c];
            if (!colId)
                continue;
            const value = row[colId];
            values.push(value === null || value === undefined ? '' : String(value));
        }
        lines.push(values.join('\t'));
    }
    return lines.join('\n');
}
// XLSX export (re-exported from xlsx.ts)
export { buildGridWorkbook, buildPivotWorkbook, exportPivotToXLSX, exportToXLSX, resolveExcelJS, } from './xlsx';
//# sourceMappingURL=index.js.map